import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PageShell } from "@/components/Navbar";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Upload, ArrowLeft } from "lucide-react";

const EMPLOYMENT = [
  ["FULL_TIME", "Full-time"], ["PART_TIME", "Part-time"], ["COMMISSION", "Commission-based"],
  ["CONTRACT", "Contract"], ["INTERNSHIP", "Internship"],
];

export default function PostRecruitment() {
  const nav = useNavigate();
  const { id } = useParams();
  const editing = !!id;
  const [loading, setLoading] = useState(editing);
  const [busy, setBusy] = useState(false);
  const [cover, setCover] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [f, setF] = useState({
    title: "", description: "", employment_type: "FULL_TIME", role_category: "",
    city: "", province: "", is_remote: false, compensation_text: "",
    organization_id: "none", visibility: "PUBLIC", application_mode: "IN_APP", external_apply_url: "",
  });

  useEffect(() => {
    api.get("/me/organizations").then((r) => setOrgs(r.data || [])).catch(() => {});
    if (editing) {
      api.get(`/recruitment/${id}`).then(({ data }) => {
        setF({
          title: data.title || "", description: data.description || "", employment_type: data.employment_type || "FULL_TIME",
          role_category: data.role_category || "", city: data.city || "", province: data.province || "",
          is_remote: !!data.is_remote, compensation_text: data.compensation_text || "",
          organization_id: data.organization?.id || "none", visibility: data.visibility || "PUBLIC",
          application_mode: data.application_mode || "IN_APP", external_apply_url: data.external_apply_url || "",
        });
      }).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
    }
  }, [id, editing]);

  const payload = () => ({ ...f, organization_id: f.organization_id === "none" ? null : f.organization_id });

  const save = async (publish) => {
    if (!f.title.trim()) { toast.error("A title is required"); return; }
    setBusy(true);
    try {
      let rid = id;
      if (editing) {
        await api.put(`/recruitment/${id}`, payload());
        if (publish) await api.patch(`/recruitment/${id}/status`, { action: "publish" });
      } else {
        const { data } = await api.post("/recruitment", { ...payload(), publish });
        rid = data.id;
      }
      if (cover && rid) {
        const fd = new FormData(); fd.append("file", cover);
        await api.post(`/recruitment/${rid}/cover`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(publish ? "Opportunity published" : "Saved");
      nav("/my-recruitment");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (loading) return <PageShell><div className="flex justify-center py-32"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div></PageShell>;

  return (
    <PageShell>
      <div className="max-w-2xl mx-auto" data-testid="post-recruitment">
        <button onClick={() => nav("/my-recruitment")} className="mb-4 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0F2C59]"><ArrowLeft className="h-4 w-4" /> My Recruitment</button>
        <h1 className="font-display text-2xl font-bold text-[#0F2C59] mb-6">{editing ? "Edit opportunity" : "Post a recruitment opportunity"}</h1>
        <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-6">
          <div><Label>Title *</Label><Input data-testid="recruitment-title-input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="rounded-xl mt-1" placeholder="e.g. Senior Sales Manager" /></div>
          <div><Label>Description</Label><Textarea data-testid="recruitment-description-input" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={5} className="rounded-xl mt-1" placeholder="Responsibilities, requirements, benefits…" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Employment type</Label>
              <Select value={f.employment_type} onValueChange={(v) => setF({ ...f, employment_type: v })}>
                <SelectTrigger data-testid="recruitment-employment-type" className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{EMPLOYMENT.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Role category</Label><Input value={f.role_category} onChange={(e) => setF({ ...f, role_category: e.target.value })} className="rounded-xl mt-1" placeholder="e.g. Sales" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>City</Label><Input value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} className="rounded-xl mt-1" /></div>
            <div><Label>Province</Label><Input value={f.province} onChange={(e) => setF({ ...f, province: e.target.value })} className="rounded-xl mt-1" /></div>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-600"><Checkbox checked={f.is_remote} onCheckedChange={(v) => setF({ ...f, is_remote: !!v })} data-testid="recruitment-remote" /> Remote-friendly</label>
          <div><Label>Compensation (free text)</Label><Input value={f.compensation_text} onChange={(e) => setF({ ...f, compensation_text: e.target.value })} className="rounded-xl mt-1" placeholder="e.g. Competitive + commission" /></div>
          <div><Label>Hiring organization (optional)</Label>
            <Select value={f.organization_id} onValueChange={(v) => setF({ ...f, organization_id: v })}>
              <SelectTrigger data-testid="recruitment-org" className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None (post as myself)</SelectItem>
                {orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Visibility</Label>
              <Select value={f.visibility} onValueChange={(v) => setF({ ...f, visibility: v })}>
                <SelectTrigger data-testid="recruitment-visibility" className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="PUBLIC">Public (anyone can discover)</SelectItem>
                  <SelectItem value="NETWORK_ONLY">Network only (signed-in professionals)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Applications</Label>
              <Select value={f.application_mode} onValueChange={(v) => setF({ ...f, application_mode: v })}>
                <SelectTrigger data-testid="recruitment-app-mode" className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="IN_APP">In-app (managed here)</SelectItem>
                  <SelectItem value="EXTERNAL_LINK">External link</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {f.application_mode === "EXTERNAL_LINK" && (
            <div><Label>External application URL *</Label><Input value={f.external_apply_url} onChange={(e) => setF({ ...f, external_apply_url: e.target.value })} className="rounded-xl mt-1" placeholder="https://…" /></div>
          )}
          <div>
            <Label>Cover image (optional)</Label>
            <label className="mt-1 flex items-center gap-2 rounded-xl border border-dashed border-slate-300 px-3 py-2.5 cursor-pointer hover:border-[#0F2C59]/40" data-testid="recruitment-cover-label">
              <Upload className="h-4 w-4 text-slate-400" />
              <span className="text-sm text-slate-500 truncate">{cover ? cover.name : "Attach a cover / ad image"}</span>
              <input type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" data-testid="recruitment-cover-input" onChange={(e) => setCover(e.target.files?.[0] || null)} />
            </label>
          </div>
          <div className="flex gap-3 pt-2">
            <Button data-testid="recruitment-save-draft" onClick={() => save(false)} disabled={busy} variant="outline" className="rounded-xl">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save draft"}</Button>
            <Button data-testid="recruitment-publish" onClick={() => save(true)} disabled={busy} className="rounded-xl bg-[#0F2C59] hover:bg-[#0F2C59]/90">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Publish"}</Button>
          </div>
        </div>
      </div>
    </PageShell>
  );
}
